import { Reducer } from 'redux';
import { update } from '../helpers/immutabilityHelper'
import * as Dtos from '../dtos/Tmd.dtos';

import {
    ACTION_HRECS_LOAD,
    ACTION_HRECS_LOAD_BY_ID,
    ACTION_HRECS_LOAD_FAILURE,
    ACTION_HRECS_LOAD_SUCCESS,

    ACTION_HRECS_SAVE,
    ACTION_HRECS_SAVE_SUCCESS,
    ACTION_HRECS_SAVE_FAILURE,

    ACTION_HRECS_CREATE,
    ACTION_HRECS_CREATE_SUCCESS,
    ACTION_HRECS_CREATE_FAILURE,

    ACTION_HRECS_VALIDATE,
    ACTION_HRECS_VALIDATE_SUCCESS,
    ACTION_HRECS_VALIDATE_FAILURE,

    ACTION_HRECS_CLEAR,
    ACTION_HRECS_FILTER,

    HrecAction
} from 'src/actions/hrec';
import { RequestState } from "../enumerations/RequestState";
import { IRequestState, IFormFilter } from "./index";
import { updateFailureIndexToId } from "../helpers/formErrorHelper";

export interface IHrecState {
    formData: Dtos.Hrec | Dtos.Hrec[],
    formState: Dtos.FormState,
    formProperties: Dtos.FormProperty[],
    validationFailures: Dtos.ResponseError[];
    lookups: Dtos.Lookup[],
    loadState: IRequestState;
    saveState: IRequestState;
    updateState: IRequestState;
    createState: IRequestState;
    validateState: IRequestState;
    formFilter: IFormFilter;
    formFilteredData: Dtos.Hrec[];
    formLength: number;
}

const initialState: IHrecState = {
    formData: undefined,
    formState: undefined,
    formProperties: undefined,
    validationFailures: undefined,
    lookups: undefined,
    loadState: {
        status: RequestState.None
    } as IRequestState,
    saveState: {
        status: RequestState.None
    } as IRequestState,
    updateState: {
        status: RequestState.None
    } as IRequestState,
    createState: {
        status: RequestState.None
    } as IRequestState,
    validateState: {
        status: RequestState.None
    } as IRequestState,
    formFilter: {
        search: "",
        showInActive: false
    },
    formFilteredData: [],
    formLength: 0
};


function loading(state: IHrecState, _isLoading: boolean): IHrecState {
    const newState: IHrecState = update(state, {
        loadState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function loadSuccess(state: IHrecState,
    formData: Dtos.Hrec | Dtos.Hrec[],
    formState: Dtos.FormState,
    formProperties: Dtos.FormProperty[],
    lookups: Dtos.Lookup[],
    responseStatus: Dtos.ResponseStatus
): IHrecState {
    const newState: IHrecState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        formProperties: {
            $set: formProperties
        },
        lookups: {
            $set: lookups
        },
        loadState: {
            $set: {
                status: RequestState.Success,
                errorCode: responseStatus ? responseStatus.errorCode : undefined,
                errorMessage: responseStatus ? responseStatus.message : undefined,
                errors: responseStatus ? responseStatus.errors : undefined,
                meta: responseStatus ? responseStatus.meta : undefined
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, formData) : undefined
        }
    });

    return filter(newState, state.formFilter);
}

function filter(state: IHrecState, formFilter: IFormFilter): IHrecState {

    const hrecs: Dtos.Hrec[] | undefined =
        state.formData instanceof Array ?
            state.formData :
            undefined;

    const formLength = hrecs ? hrecs.length : 0;

    const formFilteredData = hrecs ? hrecs.filter(p => (p.active == true || formFilter.showInActive) &&
        (formFilter.search == "" || p.fullName.toLowerCase().indexOf(formFilter.search.toLowerCase()) > -1)) : undefined;

    const newState: IHrecState = update(state, {
        formFilter: {
            $set: formFilter
        },
        formFilteredData: {
            $set: formFilteredData
        },
        formLength: {
            $set: formLength
        }
    });

    return newState;
}

function loadFailure(state: IHrecState, responseStatus: Dtos.ResponseStatus): IHrecState {
    const newState: IHrecState = update(state, {
        loadState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus.errorCode,
                errorMessage: responseStatus.message,
                errors: responseStatus.errors
            } as IRequestState
        }
    });

    return newState;
}

function clear(state: IHrecState, propertiesToClear?: string[]): IHrecState {

    if (!propertiesToClear) {
        const newState: IHrecState = update(initialState, {});
        return newState;
    }

    let stateModifier: any = {};

    propertiesToClear.forEach(propertyToClear => {
        stateModifier[propertyToClear] = { $set: initialState[propertyToClear] }
    });

    const newState = update(state, stateModifier);

    return newState;
}

function saving(state: IHrecState, _isSaving: boolean): IHrecState {
    const newState: IHrecState = update(state, {
        saveState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function saveSuccess(
    state: IHrecState,
    formData: Dtos.Hrec | Dtos.Hrec[],
    formState: Dtos.FormState,
    responseStatus: Dtos.ResponseStatus
): IHrecState {
    const newState: IHrecState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        saveState: {
            $set: {
                status: RequestState.Success,
                errorCode: responseStatus ? responseStatus.errorCode : undefined,
                errorMessage: responseStatus ? responseStatus.message : undefined,
                errors: responseStatus ? responseStatus.errors : undefined,
                meta: responseStatus ? responseStatus.meta : undefined
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, formData) : undefined
        }
    });

    return newState;
}

function saveFailure(state: IHrecState, responseStatus: Dtos.ResponseStatus): IHrecState {
    const newState: IHrecState = update(state, {
        saveState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus.errorCode,
                errorMessage: responseStatus.message,
                errors: responseStatus.errors,
                meta: responseStatus.meta
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, state.formData) : undefined
        }
    });

    return newState;
}

function creating(state: IHrecState, _isCreating: boolean): IHrecState {
    const newState: IHrecState = update(state, {
        createState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function createSuccess(
    state: IHrecState,
    formData: Dtos.Hrec,
    formState: Dtos.FormState,
    formProperties: Dtos.FormProperty[],
    lookups: Dtos.Lookup[]
): IHrecState {
    const newState: IHrecState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        formProperties: {
            $set: formProperties
        },
        lookups: {
            $set: lookups
        },
        createState: {
            $set: {
                status: RequestState.Success
            } as IRequestState
        }
    });

    return newState;
}

function createFailure(state: IHrecState, responseStatus: Dtos.ResponseStatus): IHrecState {
    const newState: IHrecState = update(state, {
        createState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus.errorCode,
                errorMessage: responseStatus.message,
                errors: responseStatus.errors
            } as IRequestState
        }
    });

    return newState;
}

function validating(state: IHrecState, _isValidating: boolean): IHrecState {
    const newState: IHrecState = update(state, {
        validateState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function validateSuccess(
    state: IHrecState,
    formData: Dtos.Hrec,
    formState: Dtos.FormState,
    responseStatus: Dtos.ResponseStatus
): IHrecState {
    const newState: IHrecState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        validateState: {
            $set: {
                status: RequestState.Success,
                errorCode: responseStatus ? responseStatus.errorCode : undefined,
                errorMessage: responseStatus ? responseStatus.message : undefined,
                errors: responseStatus ? responseStatus.errors : undefined,
                meta: responseStatus ? responseStatus.meta : undefined
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, formData) : undefined
        }
    });

    return newState;
}

function validateFailure(state: IHrecState, responseStatus: Dtos.ResponseStatus): IHrecState {
    const newState: IHrecState = update(state, {
        validateState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus.errorCode,
                errorMessage: responseStatus.message,
                errors: responseStatus.errors,
                meta: responseStatus.meta
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, state.formData) : undefined
        }
    });

    return newState;
}

const HrecReducer: Reducer<IHrecState> = (state: IHrecState = initialState, action: HrecAction) => {
    switch (action.type) {
        case ACTION_HRECS_LOAD:
        case ACTION_HRECS_LOAD_BY_ID:
            return loading(state, true);
        case ACTION_HRECS_LOAD_SUCCESS:
            return loadSuccess(state, action.formData, action.formState, action.formProperties, action.lookups, action.responseStatus);
        case ACTION_HRECS_LOAD_FAILURE:
            return loadFailure(state, action.responseStatus);
        case ACTION_HRECS_CLEAR:
            return clear(state, action.propertiesToClear);
        case ACTION_HRECS_SAVE:
            return saving(state, true);
        case ACTION_HRECS_SAVE_SUCCESS:
            return saveSuccess(state, action.formData, action.formState, action.responseStatus);
        case ACTION_HRECS_SAVE_FAILURE:
            return saveFailure(state, action.responseStatus);
        case ACTION_HRECS_CREATE:
            return creating(state, true);
        case ACTION_HRECS_CREATE_SUCCESS:
            return createSuccess(state, action.formData, action.formState, action.formProperties, action.lookups);
        case ACTION_HRECS_CREATE_FAILURE:
            return createFailure(state, action.responseStatus);
        case ACTION_HRECS_VALIDATE:
            return validating(state, true);
        case ACTION_HRECS_VALIDATE_SUCCESS:
            return validateSuccess(state, action.formData, action.formState, action.responseStatus);
        case ACTION_HRECS_VALIDATE_FAILURE:
            return validateFailure(state, action.responseStatus);
        case ACTION_HRECS_FILTER:
            return filter(state, action.formFilter);
    }

    return state;
}

export default HrecReducer;